import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Filter, RotateCcw } from 'lucide-react';
import { CaregiverFilters as FilterType } from '@/types/caregiver';

interface CaregiverFiltersProps {
  filters: FilterType;
  onFiltersChange: (filters: FilterType) => void;
  onClearFilters: () => void;
  isVisible: boolean;
  onToggleVisibility: () => void;
}

const skillOptions = [
  'Personal Care',
  'Meal Preparation',
  'Light Housekeeping',
  'Medication Reminders',
  'Transportation',
  'Companionship'
];

const certificationOptions = [
  'CPR Certified',
  'CNA Licensed',
  'OT/PT Aide',
  'First Aid'
];

const workPreferenceOptions = [
  'Has Transportation',
  'Pet Friendly',
  'Housekeeping'
];

const availabilityDayOptions = [
  'Monday',
  'Tuesday', 
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];

export function CaregiverFilters({
  filters,
  onFiltersChange,
  onClearFilters,
  isVisible,
  onToggleVisibility
}: CaregiverFiltersProps) {
  const [rateRange, setRateRange] = useState([
    filters.hourlyRateMin || 15,
    filters.hourlyRateMax || 50
  ]);

  const updateFilter = (key: keyof FilterType, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const updateArrayFilter = (key: keyof FilterType, value: string, checked: boolean) => {
    const currentArray = (filters[key] as string[]) || [];
    if (checked) {
      updateFilter(key, [...currentArray, value]);
    } else {
      updateFilter(key, currentArray.filter(item => item !== value));
    }
  };

  const handleRateChange = (values: number[]) => {
    setRateRange(values);
    onFiltersChange({
      ...filters,
      hourlyRateMin: values[0],
      hourlyRateMax: values[1]
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.verificationStatus && filters.verificationStatus !== 'all') count++;
    if (filters.caregiverType && filters.caregiverType !== 'all') count++;
    if (filters.skills?.length) count++;
    if (filters.certifications?.length) count++;
    if (filters.workPreferences?.length) count++;
    if (filters.hourlyRateMin !== undefined || filters.hourlyRateMax !== undefined) count++;
    if (filters.availabilityDays?.length) count++;
    if (filters.availabilityHours && filters.availabilityHours !== 'all') count++;
    return count;
  };

  if (!isVisible) {
    return (
      <div className="flex items-center gap-2 mb-6">
        <Button
          variant="outline" 
          onClick={onToggleVisibility}
          className="gap-2"
        >
          <Filter className="w-4 h-4" />
          Filters
          {getActiveFilterCount() > 0 && (
            <Badge variant="secondary" className="ml-1">
              {getActiveFilterCount()}
            </Badge>
          )}
        </Button>
        {getActiveFilterCount() > 0 && (
          <Button variant="ghost" size="sm" onClick={onClearFilters} className="gap-1">
            <RotateCcw className="w-3 h-3" />
            Clear
          </Button>
        )}
      </div>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Caregiver Filters
          </CardTitle>
          <div className="flex items-center gap-2">
            {getActiveFilterCount() > 0 && (
              <Button variant="ghost" size="sm" onClick={onClearFilters} className="gap-1">
                <RotateCcw className="w-3 h-3" />
                Clear All
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onToggleVisibility}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Verification Status */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Verification Status</Label>
          <Select
            value={filters.verificationStatus || 'all'}
            onValueChange={(value) => updateFilter('verificationStatus', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Caregivers</SelectItem>
              <SelectItem value="trusted_pro">Trusted Pro</SelectItem>
              <SelectItem value="basic_member">Basic Member</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Caregiver Type */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Caregiver Type</Label>
          <Select
            value={filters.caregiverType || 'all'}
            onValueChange={(value) => updateFilter('caregiverType', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="independent">Independent</SelectItem>
              <SelectItem value="agency">Agency</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Skills */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Skills & Services</Label>
          <div className="grid grid-cols-2 gap-2">
            {skillOptions.map((skill) => (
              <div key={skill} className="flex items-center space-x-2">
                <Checkbox
                  id={`skill-${skill}`}
                  checked={(filters.skills || []).includes(skill)}
                  onCheckedChange={(checked) => 
                    updateArrayFilter('skills', skill, checked as boolean)
                  }
                />
                <Label 
                  htmlFor={`skill-${skill}`}
                  className="text-sm cursor-pointer"
                >
                  {skill}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Certifications</Label>
          <div className="grid grid-cols-2 gap-2">
            {certificationOptions.map((cert) => (
              <div key={cert} className="flex items-center space-x-2">
                <Checkbox
                  id={`cert-${cert}`}
                  checked={(filters.certifications || []).includes(cert)}
                  onCheckedChange={(checked) => 
                    updateArrayFilter('certifications', cert, checked as boolean)
                  }
                />
                <Label 
                  htmlFor={`cert-${cert}`}
                  className="text-sm cursor-pointer"
                >
                  {cert}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Work Preferences */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Work Preferences</Label>
          <div className="space-y-2">
            {workPreferenceOptions.map((pref) => (
              <div key={pref} className="flex items-center space-x-2">
                <Checkbox
                  id={`pref-${pref}`}
                  checked={(filters.workPreferences || []).includes(pref)}
                  onCheckedChange={(checked) => 
                    updateArrayFilter('workPreferences', pref, checked as boolean)
                  }
                />
                <Label 
                  htmlFor={`pref-${pref}`}
                  className="text-sm cursor-pointer"
                >
                  {pref}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Hourly Rate */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">
            Hourly Rate: ${rateRange[0]} - ${rateRange[1]}
          </Label>
          <Slider
            min={15}
            max={50}
            step={1}
            value={rateRange}
            onValueChange={handleRateChange}
            className="w-full"
          />
        </div>

        {/* Availability Days */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Available Days</Label>
          <div className="grid grid-cols-2 gap-2">
            {availabilityDayOptions.map((day) => (
              <div key={day} className="flex items-center space-x-2">
                <Checkbox
                  id={`day-${day}`}
                  checked={(filters.availabilityDays || []).includes(day.toLowerCase())}
                  onCheckedChange={(checked) => 
                    updateArrayFilter('availabilityDays', day.toLowerCase(), checked as boolean)
                  }
                />
                <Label 
                  htmlFor={`day-${day}`}
                  className="text-sm cursor-pointer"
                >
                  {day}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Availability Hours */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Availability Hours</Label>
          <Select
            value={filters.availabilityHours || 'all'}
            onValueChange={(value) => updateFilter('availabilityHours', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any Hours</SelectItem>
              <SelectItem value="full-time">Full-time</SelectItem>
              <SelectItem value="part-time">Part-time</SelectItem>
              <SelectItem value="flexible">Flexible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}